import { stationMethods } from "@swa-ui/itinerary";

import {
  getAirportGroup,
  getAirportGroupDisplayName,
  getAirportGroupId,
  getAirportGroups,
} from "../utilities";

/**
 * isParentIdExists
 *
 * Parameter Example: ["MFP", "FLL", "MIA", "PBI"]
 * Return value: Boolean value true is returned if the id is present in the stationCodes passed.
 */

export const isParentIdExists = (stationCodes) =>
  getAirportGroups().some((station) => station?.id === stationCodes[0]);

/**
 * getAreaAirportValue
 *
 * Parameter Example: ["MFP", "FLL", "MIA", "PBI"] / ["FLL", "PBI"]
 * Return value: MFP is returned if isParentIdExists is true. otherwise it will get the id for the station codes passed.
 *
 * Parameter Example: ["PBI"] / "PBI"
 * Return value: "PBI" is returned if the passed value is a string/has only one element in the array.
 */

export function getAreaAirportValue(values) {
  let result;

  if (Array.isArray(values)) {
    result =
      values.length > 1
        ? isParentIdExists(values)
          ? values[0]
          : getAirportGroupId(values)
        : values[0];
  } else {
    result = values;
  }

  return result;
}

/**
 * getAreaStationName
 *
 * Parameter Example: ["MFP", "FLL", "MIA", "PBI"] / ["MIA", "PBI"]
 * Return value: Miami is returned.
 *
 * Parameter Example: "FLL"/["FLL"]
 * Return value: "FLL" is returned.
 */

export function getAreaStationName(values) {
  let result;

  if (Array.isArray(values)) {
    result =
      values.length > 1
        ? isParentIdExists(values)
          ? getAirportGroupDisplayName(values.slice(1))
          : getAirportGroupDisplayName(values)
        : values[0];
  } else {
    result = values;
  }

  return result;
}

/**
 * getFromOrToValue
 *
 * Parameter Example: ["MFP", "FLL", "MIA", "PBI"] / ["MIA", "PBI"]
 * Return value: ["FLL", "MIA", "PBI"] is returned if isParentIdExists is true. ["MIA", "PBI"] is returned if isParentIdExists is false.
 */

export function getFromOrToValue(values) {
  return isParentIdExists(values) ? values.slice(1) : values;
}

/**
 * getLfcStationValue
 *
 * Parameter Example: ["MIA", "PBI"] / ["MIA"]
 * Return value: empty string/"MIA" is returned if passed value is an array.
 *
 * Parameter Example: "PBI"
 * Return value: "PBI" is returned if the passed value is a string.
 */

export function getLfcStationValue(values) {
  return Array.isArray(values) ? (values.length > 1 ? "" : values[0]) : values;
}

/**
 * getStationDisplayName
 *
 * Parameter Example: ["MFP", "FLL", "MIA", "PBI"] / ["MIA", "PBI"]
 * Return value: Miami is returned.
 *
 * Parameter Example: "FLL"/["FLL"]
 * Return value: Fort Lauderdale is returned.
 */

export function getStationDisplayName(values) {
  let result;

  if (Array.isArray(values)) {
    result =
      values.length > 1
        ? isParentIdExists(values)
          ? getAirportGroupDisplayName(values.slice(1))
          : getAirportGroupDisplayName(values)
        : stationMethods.getShortDisplayStationName(values[0]);
  } else {
    result = stationMethods.getShortDisplayStationName(values);
  }

  return result;
}

/**
 * getStationValue
 *
 * Parameter Example: ["MIA", "PBI"] / ["MIA"]
 * Return value: ["MIA", "PBI"]/"MIA" is returned if passed value is an array.
 *
 * Parameter Example: "PBI"
 * Return value: "PBI" is returned if the passed value is a string.
 */

export function getStationValue(values) {
  return Array.isArray(values) ? (values.length > 1 ? values : values[0]) : values;
}

/**
 * parseMultiStationField
 *
 * Parses the to/from fields, as a comma-separated string, into an array with the selected stations
 * Adds the group ID to the front of the returned array if the field contains the entire group
 *
 * Parameter Example: (undefined, undefined)
 * Return value: undefined
 *
 * Parameter Example: (undefined, "DAL")
 * Return value: "DAL"
 *
 * Parameter Example: ("LAX", "DAL")
 * Return value: "DAL"
 *
 * Parameter Example: ("LAX", "LOS")
 * Return value: ["LAX"]
 *
 * Parameter Example: ("BUR,LAX", "LOS")
 * Return value: ["BUR", "LAX"]
 *
 * Parameter Example: ("LAX,BUR,ONT,SNA,LGB", "LOS")
 * Return value: ["LOS", "LAX", "BUR", "ONT", "SNA", "LGB"]
 */

export function parseMultiStationField(field, airportCode) {
  let parsedAirportCode = airportCode;

  if (field && parsedAirportCode && isParentIdExists([parsedAirportCode])) {
    parsedAirportCode = field.split(",").sort();
    const group = getAirportGroup(parsedAirportCode);

    if (group && JSON.stringify(group.children.sort()) === JSON.stringify(parsedAirportCode)) {
      parsedAirportCode.unshift(group.id);
    }
  }

  return parsedAirportCode;
}
